import { useState, useEffect, useRef } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { createHistory, updateHistoryByTaskId } from '../../services/history';
import {
  getVideoModelConfig,
  createVideoTask,
  getVideoTaskStatus,
  getVideoUrlFromTaskResult,
  type VideoModelConfig,
} from '../../services/video';
import { getSystemModels, getMyModels, type VirtualModelItem } from '../../services/virtualModels';

/** 单次生成消耗积分（与 tool_credit_costs 中 sora2-video 可保持一致） */
const VIDEO_CREDITS_PER_USE = 10;

export default function Sora2VideoPage() {
  const [activeMenu, setActiveMenu] = useState('视频生成');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState('5秒');
  const [selectedRatio, setSelectedRatio] = useState('9:16');
  const [prompt, setPrompt] = useState('');
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [keywordProduct, setKeywordProduct] = useState('');
  const [keywordFeatures, setKeywordFeatures] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedModels, setSelectedModels] = useState<Array<{ id: number | string; name: string }>>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [videoModelConfig, setVideoModelConfig] = useState<VideoModelConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoTaskId, setVideoTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** 虚拟模特来源：系统模特 | 我的模特 */
  const [modelTab, setModelTab] = useState<'system' | 'my'>('system');
  const [myModels, setMyModels] = useState<VirtualModelItem[]>([]);
  const [myModelsLoading, setMyModelsLoading] = useState(false);

  const systemModels = getSystemModels();
  const displayModels = modelTab === 'system' ? systemModels : myModels;

  const durations = ['5秒', '10秒'];
  const ratios = [
    { label: '竖屏 9:16', value: '9:16' },
    { label: '横屏 16:9', value: '16:9' }
  ];

  // 加载用户与后台配置的视频模型（如 doubao-seedance-1-5-pro-251215）
  useEffect(() => {
    const load = async () => {
      const [currentUser, modelConfig] = await Promise.all([
        getCurrentUser(),
        getVideoModelConfig(),
      ]);
      setUser(currentUser);
      setVideoModelConfig(modelConfig ?? null);
    };
    load();
  }, []);

  // 切换「我的模特」时拉取当前用户的模特列表
  useEffect(() => {
    if (modelTab !== 'my' || !user?.id) {
      if (modelTab !== 'my') setMyModels([]);
      return;
    }
    setMyModelsLoading(true);
    getMyModels(user.id).then((list) => {
      setMyModels(list);
      setMyModelsLoading(false);
    }).catch(() => setMyModelsLoading(false));
  }, [modelTab, user?.id]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleModelSelect = (model: VirtualModelItem) => {
    const isSelected = selectedModels.some(m => m.id === model.id);
    if (isSelected) {
      setSelectedModels(selectedModels.filter(m => m.id !== model.id));
    } else {
      setSelectedModels([...selectedModels, { id: model.id, name: model.name }]);
    }
  };

  const handleGenerateKeywords = () => {
    if (!keywordProduct || !keywordFeatures) {
      return;
    }
    
    setIsGenerating(true);
    
    // 模拟AI生成过程
    setTimeout(() => {
      // 根据用户输入生成结构化的分镜脚本
      const duration = parseInt(selectedDuration);
      const scenes = generateStoryboard('', keywordProduct, keywordFeatures, duration);
      
      setPrompt(scenes);
      setIsGenerating(false);
      setShowKeywordModal(false);
      setKeywordProduct('');
      setKeywordFeatures('');
    }, 2000);
  };

  const handleGenerate = async () => {
    setError(null);
    if (!prompt.trim()) {
      setError('请输入描述词');
      return;
    }
    if (!user) {
      setError('请先登录');
      return;
    }
    if (!videoModelConfig?.model_name) {
      setError('视频模型未配置或未启用，请在后台配置并启用视频模型（阿里 wanx / 火山 Seedance 等）');
      return;
    }
    if (videoModelConfig.provider !== 'alibaba' && !videoModelConfig.api_key) {
      setError('当前视频模型缺少可用密钥。请启用阿里视频模型，或重新配置火山引擎/钱多多密钥');
      return;
    }

    setIsGenerating(true);
    try {
      const deductResult = await deductCredits(
        user.id,
        VIDEO_CREDITS_PER_USE,
        '使用视频生成（Seedance）'
      );
      if (!deductResult.success) {
        setError(deductResult.error || '积分不足，请前往充值');
        setIsGenerating(false);
        return;
      }

      const durationNum = parseInt(selectedDuration.replace(/秒/g, ''), 10) || 5;
      // 参考图：优先用手动上传的首图，否则用选中的第一个虚拟模特的图片
      const allModelsForLookup = [...systemModels, ...myModels];
      const firstSelectedModel = selectedModels.length > 0
        ? allModelsForLookup.find((m) => m.id === selectedModels[0].id)
        : null;
      const referenceImageUrl = selectedImage || firstSelectedModel?.image || undefined;

      const taskResult = await createVideoTask(videoModelConfig, {
        prompt: prompt.trim(),
        duration: durationNum,
        ratio: selectedRatio,
        imageUrl: referenceImageUrl,
        camera_fixed: false,
      });

      if (taskResult?.id) {
        await createHistory(user.id, {
          type: 'video',
          category: 'sora2-video',
          status: 'pending',
          task_id: taskResult.id,
          prompt: prompt.trim(),
          input_images: referenceImageUrl ? [referenceImageUrl] : undefined,
          settings: { duration: durationNum, ratio: selectedRatio },
          credits_used: VIDEO_CREDITS_PER_USE,
        });
        setVideoTaskId(taskResult.id);
        setShowSuccessModal(true);
      } else {
        setError('视频任务创建失败，请稍后重试');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '视频生成失败';
      setError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const checkTaskStatus = async (taskId: string) => {
    if (!videoModelConfig?.api_key) return null;
    try {
      const taskData = await getVideoTaskStatus(videoModelConfig, taskId);
      if (!taskData) return null;
      setTaskStatus(taskData.status ?? null);
      switch (taskData.status) {
        case 'queued':
          setProgress(20);
          break;
        case 'running':
          setProgress((p) => Math.min(p + 10, 90));
          break;
        case 'succeeded': {
          setProgress(100);
          const videoUrl = getVideoUrlFromTaskResult(taskData);
          if (videoUrl) setGeneratedVideoUrl(videoUrl);
          stopPolling();
          if (user && videoUrl) {
            await updateHistoryByTaskId(user.id, taskId, { status: 'succeeded', output_url: videoUrl });
          }
          break;
        }
        case 'failed':
        case 'expired':
          setProgress(0);
          stopPolling();
          if (user) {
            await updateHistoryByTaskId(user.id, taskId, { status: taskData.status });
          }
          break;
        default:
          break;
      }
      return taskData;
    } catch (e) {
      console.error('查询任务状态失败', e);
      return null;
    }
  };

  const startPolling = () => {
    if (!videoTaskId) return;
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    checkTaskStatus(videoTaskId);
    pollingIntervalRef.current = setInterval(() => checkTaskStatus(videoTaskId), 3000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => () => { stopPolling(); }, []);

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    startPolling();
  };

  const generateStoryboard = (brand: string, product: string, features: string, totalDuration: number) => {
    const brandPrefix = brand ? `${brand} ` : '';
    // 根据时长动态生成分镜数量
    const sceneCount = Math.min(Math.floor(totalDuration / 1.5), 9);
    let storyboard = '';
    let currentTime = 0;
    
    const scenes = [
      {
        type: '全景，缓慢推近',
        description: `电影感全景镜头，阳光柔和的场景，一位用户惬意地注视着${product}，镜头缓慢推近，突出${brandPrefix}产品精致的设计和科技感的外观。`
      },
      {
        type: '中景，跟拍',
        description: `中景跟拍镜头，用户开始使用${product}，${features}，脸上浮现出被产品体验打动的愉悦笑容。`
      },
      {
        type: '特写，快速切换',
        description: `快速切换的特写镜头，用户的手指在${product}上操作，界面响应灵敏，带有微妙的动态光效，展现流畅交互。`
      },
      {
        type: '中景，固定镜头',
        description: `固定机位中景，用户在日常场景中使用${product}，${features}，沉浸在高效便捷的体验中，与周围环境形成对比。`
      },
      {
        type: '近景，跟随拍摄',
        description: `手持感近景跟随，用户在移动中使用${product}，操作清晰流畅，背景虚化，充满活力和现代感。`
      },
      {
        type: '特写，静态展示',
        description: `产品特写静态展示，${brandPrefix}${product}的细节特写，质感卓越，设计精美，细节清晰可见。`
      },
      {
        type: '中景，移动镜头',
        description: `流畅的移动镜头，用户与朋友分享${product}，朋友体验后露出惊喜表情，两人相视而笑，体现社交乐趣和产品的便捷性。`
      },
      {
        type: '近景，固定镜头',
        description: `固定镜头近景，${product}展示其核心功能，${features}，象征便捷生活和科技感。`
      },
      {
        type: '特写，定格',
        description: `产品Logo大特写，${brand ? `${brand}字样清晰浮现` : '产品标识清晰浮现'}，背景纯净，同时屏幕下方出现广告语："${brandPrefix}${product}，${features.split('，')[0]}"，画面定格。`
      }
    ];

    for (let i = 0; i < sceneCount; i++) {
      const scene = scenes[i];
      const sceneDuration = i === sceneCount - 1 
        ? totalDuration - currentTime 
        : Math.round((totalDuration / sceneCount) * 10) / 10;
      const endTime = Math.round((currentTime + sceneDuration) * 10) / 10;
      
      storyboard += `分镜${i + 1}（${scene.type}） ${currentTime}-${endTime}秒\n`;
      storyboard += `提示词：${scene.description}\n\n`;
      
      currentTime = endTime;
    }

    return storyboard.trim();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">视频生成</h1>
            <p className="text-gray-400 text-sm mb-8">
              输入描述词，可选上传参考图，选择视频时长与比例。使用后台配置的视频生成模型（如 Seedance 1.5 Pro）生成视频。
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-400 shrink-0"></i>
                <p className="text-red-400 text-sm">{error}</p>
                <button type="button" onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-white">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            {videoTaskId && (
              <div className="bg-[#1a1a1a] border border-gray-800 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">视频生成进度</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>状态：{taskStatus === 'succeeded' ? '已完成' : taskStatus === 'failed' || taskStatus === 'expired' ? '失败/已过期' : '处理中'}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-[#0a0a0a] rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                {generatedVideoUrl && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-400 mb-2">生成结果：</p>
                    <video src={generatedVideoUrl} controls className="max-w-full rounded-lg max-h-80" />
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Upload Section */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-line text-blue-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">参考图（可选）</h3>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                    {selectedImage ? (
                      <div className="relative">
                        <img src={selectedImage} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                        <button
                          onClick={() => setSelectedImage(null)}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-white"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-add-line text-blue-400 text-3xl"></i>
                        </div>
                        <p className="text-gray-400 mb-2">点击上传文件</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Prompt Section */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-edit-line text-purple-400 text-lg"></i>
                      <h3 className="text-lg font-semibold">描述词</h3>
                    </div>
                    <button 
                      onClick={() => setShowKeywordModal(true)}
                      className="px-4 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-sm hover:border-blue-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-magic-line mr-2"></i>
                      AI生成产品关键词
                    </button>
                  </div>
                  
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="描述你想要生成的视频，比如：一位女性，穿着优雅的礼服，站在豪华的宫殿中，阳光透过窗户洒在她身上，她微笑着转身..."
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl p-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    rows={6}
                  />
                  
                  <div className="flex items-center justify-between mt-4">
                    {selectedModels.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400">参考示例图：</span>
                        {selectedModels.map((model) => (
                          <div
                            key={model.id}
                            className="flex items-center gap-2 bg-[#0a0a0a] border border-gray-700 rounded-full px-3 py-1"
                          >
                            <span className="text-xs text-gray-300">@{model.name}</span>
                            <button
                              onClick={() => setSelectedModels(selectedModels.filter(m => m.id !== model.id))}
                              className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <span className="text-xs text-gray-500 ml-auto">{prompt.length} / 2000</span>
                  </div>
                </div>

                {/* Video Ratio Selection */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-aspect-ratio-line text-cyan-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">视频尺寸</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {ratios.map((ratio) => (
                      <button
                        key={ratio.value}
                        onClick={() => setSelectedRatio(ratio.value)}
                        className={`py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedRatio === ratio.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration Selection - 排版与视频尺寸一致：两列等分铺满 */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-time-line text-green-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">视频时长</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 w-full">
                    {durations.map((duration) => (
                      <button
                        key={duration}
                        onClick={() => setSelectedDuration(duration)}
                        className={`w-full py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          selectedDuration === duration
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {duration}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button 
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className={`w-full py-4 font-semibold rounded-xl transition-all shadow-lg cursor-pointer whitespace-nowrap ${
                    isGenerating || !prompt.trim()
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-blue-500/20 hover:shadow-blue-500/40'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin inline-block mr-2"></i>
                      提交中…
                    </>
                  ) : (
                    <>立即生成 <span className="opacity-80 text-sm ml-1">（{VIDEO_CREDITS_PER_USE} 积分/次）</span></>
                  )}
                </button>
              </div>

              {/* Right Column - Virtual Models */}
              <div className="lg:col-span-1">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 sticky top-6">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-user-star-line text-purple-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">虚拟模特</h3>
                  </div>

                  <p className="text-xs text-gray-400 mb-4">
                    参考三维建模库，有效的提高三维建模，或者选择已有模特进行三维建模
                  </p>

                  <div className="mb-4">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setModelTab('system')}
                        className={`flex-1 px-3 py-2 text-xs rounded-lg font-medium cursor-pointer whitespace-nowrap transition-colors ${
                          modelTab === 'system'
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        系统模特
                      </button>
                      <button
                        type="button"
                        onClick={() => setModelTab('my')}
                        className={`flex-1 px-3 py-2 text-xs rounded-lg font-medium cursor-pointer whitespace-nowrap transition-colors ${
                          modelTab === 'my'
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        我的模特
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                    {myModelsLoading ? (
                      <div className="col-span-3 flex items-center justify-center py-12 text-gray-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        加载中…
                      </div>
                    ) : modelTab === 'my' && !user ? (
                      <div className="col-span-3 py-12 text-center text-gray-400 text-sm">
                        请先登录后查看我的模特
                      </div>
                    ) : modelTab === 'my' && displayModels.length === 0 ? (
                      <div className="col-span-3 py-12 text-center text-gray-400 text-sm">
                        暂无我的模特，去
                        <button type="button" onClick={() => window.REACT_APP_NAVIGATE?.('/virtual-models')} className="text-blue-400 hover:underline ml-1 cursor-pointer">虚拟模特库</button>
                        创建
                      </div>
                    ) : (
                    displayModels.map((model) => {
                      const isSelected = selectedModels.some(m => m.id === model.id);
                      return (
                        <div
                          key={model.id}
                          className="cursor-pointer group"
                          onClick={() => handleModelSelect(model)}
                        >
                          <div className={`relative rounded-lg overflow-hidden mb-2 border-2 transition-all ${
                            isSelected 
                              ? 'border-blue-500' 
                              : 'border-transparent group-hover:border-blue-500'
                          }`}>
                            <div className="w-full h-32">
                              <img
                                src={model.image}
                                alt={model.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            </div>
                            <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity ${
                              isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                            }`}>
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                  <i className="ri-check-line text-white text-sm"></i>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className={`text-xs text-center transition-colors truncate ${
                            isSelected ? 'text-blue-400 font-medium' : 'text-gray-400 group-hover:text-white'
                          }`}>
                            {model.name}
                          </p>
                        </div>
                      );
                    })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Keyword Generation Modal */}
      {showKeywordModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-2xl w-full border border-gray-800 relative">
            <button
              onClick={() => setShowKeywordModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <i className="ri-video-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold">AI生成产品关键词</h2>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              填写核心信息，AI将自动生成符合{selectedDuration}视频的专业分镜脚本
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                  <i className="ri-product-hunt-line text-purple-400"></i>
                  <span>产品</span>
                </label>
                <input
                  type="text"
                  value={keywordProduct}
                  onChange={(e) => setKeywordProduct(e.target.value)}
                  placeholder="例如：AI智能手表"
                  className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                  <i className="ri-star-line text-orange-400"></i>
                  <span>核心功能 / 感受</span>
                </label>
                <textarea
                  value={keywordFeatures}
                  onChange={(e) => setKeywordFeatures(e.target.value)}
                  placeholder="描述产品的主要卖点、使用场景或带给用户的感受。例如：语音处理工作问题功能，高效工作伙伴"
                  className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  rows={4}
                  maxLength={500}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {keywordFeatures.length} / 500
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowKeywordModal(false)}
                className="flex-1 px-6 py-3 bg-[#0a0a0a] border border-gray-700 rounded-lg text-sm font-medium hover:border-gray-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleGenerateKeywords}
                disabled={!keywordProduct || !keywordFeatures || isGenerating}
                className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  keywordProduct && keywordFeatures && !isGenerating
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isGenerating ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>生成中...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-magic-line"></i>
                    <span>立即生成</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">10 点</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              
              <h2 className="text-2xl font-bold mb-3">提交成功</h2>
              
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                任务已提交成功，将自动轮询状态；约数分钟后可在本页、素材空间或历史记录中查看生成结果。
              </p>

              <button
                onClick={handleSuccessConfirm}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 cursor-pointer whitespace-nowrap"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
}
